import { defineStore } from 'pinia'
import Events from '@/api/Events'
import sourcesData from '@/data/sources.json'
import enrichmentsData from '@/data/enrichments.json'

export const useDataSourcesStore = defineStore('dataSources', {
  state: () => ({
    sources: sourcesData.items,
    sourceGroups: sourcesData.groups,
    enrichments: enrichmentsData.items,
    enrichmentGroups: enrichmentsData.groups,
    fieldMappings: {},
    loading: false,
    error: null,
    initialized: false,
    // Log type selection state — empty + noneSelected=false means "all" (no filtering)
    selectedLogTypes: [],
    noneSelected: false,
  }),

  getters: {
    activeSources: (state) => state.sources.filter(s => s.active),
    activeEnrichments: (state) => state.enrichments.filter(e => e.active),

    groupedSources: (state) => {
      const grouped = []
      for (const group of state.sourceGroups) {
        grouped.push({
          ...group,
          items: state.sources.filter(s => s.group === group.id),
        })
      }
      const ungrouped = state.sources.filter(s => !s.group)
      return { groups: grouped, ungrouped }
    },

    groupedEnrichments: (state) => {
      const grouped = []
      for (const group of state.enrichmentGroups) {
        grouped.push({
          ...group,
          items: state.enrichments.filter(e => e.group === group.id),
        })
      }
      const ungrouped = state.enrichments.filter(e => !e.group)
      return { groups: grouped, ungrouped }
    },

    // All log types from field mappings
    allLogTypes: (state) => Object.keys(state.fieldMappings).sort(),

    // Groups that have at least one active source item (used by the selector)
    activeSourceGroups(state) {
      return state.sourceGroups.filter(g =>
        state.sources.some(s => s.group === g.id && s.active)
      )
    },

    // Map of source group ID → log types. For now all log types belong to every active group.
    // Ready for future API-driven per-source mapping.
    sourceLogTypeMap(state) {
      const logTypes = Object.keys(state.fieldMappings).sort()
      const map = {}
      for (const group of state.sourceGroups) {
        const hasActive = state.sources.some(s => s.group === group.id && s.active)
        if (hasActive) {
          map[group.id] = logTypes
        }
      }
      return map
    },

    // The log types to send to the API. Empty = all (no filtering).
    effectiveLogTypes: (state) => state.selectedLogTypes,

    // Whether everything is selected (default state)
    isAllSelected: (state) => state.selectedLogTypes.length === 0 && !state.noneSelected,

    // Whether nothing is selected (can't search)
    hasNoSelection: (state) => state.noneSelected,

    // Serialize current selection as a URL param string (empty when "all")
    serializedSources() {
      if (this.isAllSelected || this.hasNoSelection) return ''
      const segments = []
      for (const group of this.activeSourceGroups) {
        const groupLts = this.sourceLogTypeMap[group.id] || []
        const selected = this.selectedLogTypes.filter(lt => groupLts.includes(lt))
        if (selected.length === 0) continue
        if (selected.length === groupLts.length) {
          segments.push(group.id)
        } else {
          segments.push(`${group.id}:${selected.join(',')}`)
        }
      }
      return segments.length > 0 ? segments.join(';') : ''
    },

    // Humanized label for the selector button
    selectorLabel() {
      if (this.noneSelected) return 'None'
      if (this.selectedLogTypes.length === 0) return 'All Sources'

      const sourcesWithSelection = []

      for (const group of this.activeSourceGroups) {
        const groupLogTypes = this.sourceLogTypeMap[group.id] || []
        const selectedForGroup = this.selectedLogTypes.filter(lt => groupLogTypes.includes(lt))
        if (selectedForGroup.length > 0) {
          sourcesWithSelection.push({ group, count: selectedForGroup.length, total: groupLogTypes.length })
        }
      }

      if (sourcesWithSelection.length === 0) return 'All Sources'

      if (sourcesWithSelection.length === 1) {
        const { group, count, total } = sourcesWithSelection[0]
        if (count === total) return group.label
        if (count === 1) {
          const logType = this.selectedLogTypes.find(lt =>
            (this.sourceLogTypeMap[group.id] || []).includes(lt)
          )
          return `${group.label} \u00b7 ${logType}`
        }
        return `${group.label} \u00b7 ${count} logs`
      }

      // Multiple sources
      const totalSelected = this.selectedLogTypes.length
      const totalAll = this.allLogTypes.length
      if (totalSelected === totalAll) return `${sourcesWithSelection.length} Sources`
      return `${sourcesWithSelection.length} Sources \u00b7 ${totalSelected} logs`
    },
  },

  actions: {
    async init() {
      if (this.initialized) return
      this.loading = true
      this.error = null
      try {
        await Promise.all([
          this.fetchSources(),
          this.fetchEnrichments(),
          this.fetchFieldMappings(),
        ])
        this.initialized = true
      } catch (err) {
        this.error = err.message || 'Failed to load data sources'
      } finally {
        this.loading = false
      }
    },

    async fetchSources() {
      // Currently using static JSON data; swap to API call later
      this.sourceGroups = sourcesData.groups
      this.sources = sourcesData.items
    },

    async fetchEnrichments() {
      // Currently using static JSON data; swap to API call later
      this.enrichmentGroups = enrichmentsData.groups
      this.enrichments = enrichmentsData.items
    },

    async fetchFieldMappings() {
      const response = await Events.getFields()
      if (response.data) {
        this.fieldMappings = response.data
      }
    },

    // Toggle all log types for a source
    toggleSource(sourceId) {
      const sourceLogTypes = this.sourceLogTypeMap[sourceId] || []
      if (sourceLogTypes.length === 0) return

      // If none selected, re-select this source's logs
      if (this.noneSelected) {
        this.selectedLogTypes = [...sourceLogTypes]
        this.noneSelected = false
        // If everything is now selected, reset to empty (= all)
        if (this.selectedLogTypes.length >= this.allLogTypes.length) {
          this.selectedLogTypes = []
        }
        return
      }

      // Treat empty selectedLogTypes as "all selected"
      const isAllMode = this.selectedLogTypes.length === 0
      const allSelected = isAllMode || sourceLogTypes.every(lt => this.selectedLogTypes.includes(lt))

      if (allSelected) {
        if (isAllMode) {
          // Transition from implicit "all" to explicit: select everything except this source
          this.selectedLogTypes = this.allLogTypes.filter(lt => !sourceLogTypes.includes(lt))
        } else {
          // Deselect all of this source's log types
          this.selectedLogTypes = this.selectedLogTypes.filter(lt => !sourceLogTypes.includes(lt))
        }
      } else {
        // Select all of this source's log types
        const toAdd = sourceLogTypes.filter(lt => !this.selectedLogTypes.includes(lt))
        this.selectedLogTypes = [...this.selectedLogTypes, ...toAdd]
      }

      // Check if nothing is left
      if (this.selectedLogTypes.length === 0) {
        this.noneSelected = true
        return
      }

      // If everything is now selected, reset to empty (= all)
      if (this.selectedLogTypes.length >= this.allLogTypes.length) {
        this.selectedLogTypes = []
      }
      this.noneSelected = false
    },

    // Toggle a single log type
    toggleLogType(logType) {
      // If none selected, select just this one
      if (this.noneSelected) {
        this.selectedLogTypes = [logType]
        this.noneSelected = false
        return
      }

      // Transition from implicit "all" to explicit: select everything except this one
      if (this.selectedLogTypes.length === 0) {
        this.selectedLogTypes = this.allLogTypes.filter(lt => lt !== logType)
        this.noneSelected = false
        return
      }

      const idx = this.selectedLogTypes.indexOf(logType)
      if (idx === -1) {
        this.selectedLogTypes = [...this.selectedLogTypes, logType]
      } else {
        this.selectedLogTypes = this.selectedLogTypes.filter(lt => lt !== logType)
      }

      // Check if nothing is left
      if (this.selectedLogTypes.length === 0) {
        this.noneSelected = true
        return
      }

      // If everything is now selected, reset to empty (= all)
      if (this.selectedLogTypes.length >= this.allLogTypes.length) {
        this.selectedLogTypes = []
      }
      this.noneSelected = false
    },

    // Reset to all (no filtering)
    resetSelection() {
      this.selectedLogTypes = []
      this.noneSelected = false
    },

    // Select exactly one log type, replacing the entire selection
    selectExclusiveLogType(logType) {
      this.selectedLogTypes = [logType]
      this.noneSelected = false
    },

    // Select all log types for a single source group, replacing the entire selection
    selectExclusiveSource(sourceId) {
      const sourceLogTypes = this.sourceLogTypeMap[sourceId] || []
      this.selectedLogTypes = [...sourceLogTypes]
      this.noneSelected = false
      // If result equals all log types, reset to implicit all
      if (this.selectedLogTypes.length >= this.allLogTypes.length) {
        this.selectedLogTypes = []
      }
    },

    // Check if all log types for a source are selected
    isSourceFullySelected(sourceId) {
      if (this.noneSelected) return false
      if (this.selectedLogTypes.length === 0) return true
      const sourceLogTypes = this.sourceLogTypeMap[sourceId] || []
      return sourceLogTypes.length > 0 && sourceLogTypes.every(lt => this.selectedLogTypes.includes(lt))
    },

    // Check if some (but not all) log types for a source are selected
    isSourcePartiallySelected(sourceId) {
      if (this.selectedLogTypes.length === 0) return false
      const sourceLogTypes = this.sourceLogTypeMap[sourceId] || []
      const selectedCount = sourceLogTypes.filter(lt => this.selectedLogTypes.includes(lt)).length
      return selectedCount > 0 && selectedCount < sourceLogTypes.length
    },
  },
})
